package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableLongStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.milta.core.localization.CommonStrings
import com.milta.core.ui.component.ProcessingProgress
import com.milta.core.ui.component.ProcessingStage
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Clock

/**
 * Retour 26/10 (maquette W1) — the real progress of an import on the "Ajouter une tournée" drop
 * zone: the same 4 steps as the mobile analysis card, the active one with "En cours depuis m:ss",
 * the reading's duration and the deliveries found once done, the address check with its bar.
 */

/** Current time, ticking every second while shown — drives the chrono and "En cours depuis". */
@Composable
internal fun rememberTickingNow(): Long {
    var now by remember { mutableLongStateOf(Clock.System.now().toEpochMilliseconds()) }
    LaunchedEffect(Unit) {
        while (true) {
            delay(1_000)
            now = Clock.System.now().toEpochMilliseconds()
        }
    }
    return now
}

/** "1:31" */
internal fun formatChrono(seconds: Long): String {
    val s = seconds.coerceAtLeast(0)
    return "${s / 60}:${(s % 60).toString().padStart(2, '0')}"
}

/** Label of the active step: "Lecture du document" while Pending or Reading, then "Vérification des adresses". */
internal fun ProcessingProgress.activeLabel(strings: CommonStrings): String =
    if (stage == ProcessingStage.Geocoding) strings.processingStepChecking else strings.processingStepReading

@Composable
internal fun WebImportProgressSteps(progress: ProcessingProgress, strings: CommonStrings, nowEpochMillis: Long) {
    val labels = listOf(strings.processingStepSent, strings.processingStepReading, strings.processingStepFinding, strings.processingStepChecking)
    val activeIndex = if (progress.stage == ProcessingStage.Geocoding) 3 else 1
    Div(attrs = {
        style {
            property("display", "flex"); property("flex-direction", "column"); property("gap", "10px")
            property("text-align", "left"); property("width", "100%"); property("max-width", "380px"); property("margin", "14px auto 4px")
        }
    }) {
        labels.forEachIndexed { index, label ->
            val state = when {
                index < activeIndex -> WebStepState.Done
                index == activeIndex -> WebStepState.Active
                else -> WebStepState.Pending
            }
            // Step 1: no detail, the file name is already shown above (W1).
            val detail = if (index == 0) null else progress.stepDetail(index, strings, nowEpochMillis)
            val bar = if (index == 3) progress.addressProgress(strings) else null
            Div(attrs = { style { property("display", "flex"); property("gap", "10px"); property("align-items", "flex-start") } }) {
                StepDot(state)
                Div(attrs = { style { property("flex", "1"); property("min-width", "0") } }) {
                    Div(attrs = {
                        style {
                            property("font-size", "13.5px"); property("font-weight", "600")
                            property("color", if (state == WebStepState.Pending) WebTextSecondary else WebTextPrimary)
                        }
                    }) { Text(label) }
                    if (detail != null) {
                        val highlighted = index == 2
                        Div(attrs = {
                            style {
                                property("font-size", "12px"); property("margin-top", "2px")
                                property("color", if (highlighted) WebSuccessInk else WebTextSecondary)
                                if (highlighted) property("font-weight", "600")
                            }
                        }) { Text(detail) }
                    }
                    if (bar != null) {
                        Div(attrs = {
                            style {
                                property("height", "6px"); property("border-radius", "999px"); property("background", WebSurfaceDim)
                                property("overflow", "hidden"); property("margin", "8px 0 5px")
                            }
                        }) {
                            Div(attrs = {
                                style {
                                    property("height", "100%"); property("width", "${bar.percent}%")
                                    property("background", WebPrimary); property("border-radius", "999px")
                                }
                            }) {}
                        }
                        Div(attrs = { style { property("display", "flex"); property("justify-content", "space-between"); property("font-size", "12px") } }) {
                            Span(attrs = { style { property("color", WebTextSecondary); property("font-variant-numeric", "tabular-nums") } }) {
                                Text(bar.progressTemplate.replace("%1", bar.sentLabel).replace("%2", bar.totalLabel))
                            }
                            Span(attrs = { style { property("color", WebInProgressInk); property("font-weight", "700"); property("font-variant-numeric", "tabular-nums") } }) {
                                Text("${bar.percent} %")
                            }
                        }
                    }
                }
            }
        }
    }
}

private enum class WebStepState { Done, Active, Pending }

@Composable
private fun StepDot(state: WebStepState) {
    Div(attrs = {
        style {
            property("width", "22px"); property("height", "22px"); property("border-radius", "50%"); property("flex-shrink", "0")
            property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
            property("box-sizing", "border-box")
            when (state) {
                WebStepState.Done -> { property("background", WebPrimary); property("color", "#FFFFFF") }
                WebStepState.Active -> property("background", WebActiveTint)
                WebStepState.Pending -> property("background", WebSurfaceDim)
            }
        }
    }) {
        when (state) {
            WebStepState.Done -> Span(attrs = { style { property("font-size", "12px"); property("font-weight", "800") } }) { Text("✓") }
            WebStepState.Active -> WebSpinner(size = 12, strokeWidth = 2)
            WebStepState.Pending -> Unit
        }
    }
}

/** Title of the analysing drop zone: "Vérification des adresses…" with the global chrono (R7). */
@Composable
internal fun WebImportProgressTitle(title: String, chrono: String?) {
    Div(attrs = { style { property("color", WebTextPrimary); property("font-size", "19px"); property("font-weight", "700") } }) {
        Text(title)
        if (chrono != null) {
            Span(attrs = {
                style {
                    property("font-size", "13px"); property("font-weight", "600"); property("color", WebTextSecondary)
                    property("margin-left", "8px"); property("font-variant-numeric", "tabular-nums"); padding(0.px)
                }
            }) { Text(chrono) }
        }
    }
}
